import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl'
import { openNewTab } from '@/shared/utils/openLink'
import _get from 'lodash/get';
import Table, { DateCell, TextCell, TxHashCell } from '@/shared/ui/Table';
import DownloadCsv from '@/entities/DownloadCsv';
import StatusIcon from '@/shared/ui/StatusIcon';
import NewTabLink from '@/shared/ui/NewTabLink';
import LoadMore from '@/shared/ui/LoadMore';
import { usePagination } from '@/shared/hooks/usePagination';
import { formatChartDate } from '@/shared/utils/dateFormatters';
import { 
  SORT_BY,
  REJECTED_STATUSES,
  ORDER,
  LIMIT,
  getStatusIconVariant, 
  getStatusMessage,
} from '~/shared/Payouts';
import { useGetTransactionsInfiniteSharedQuery } from '@/shared/api/Payouts';
import { useExplorer } from '@/processes/PoolStats';
import { useMinerAccount } from "@/processes/MinerAccounts";

type PayoutsPoolTableProps = {
  className?: string;
  csvRef?: any;
}

const PayoutsPoolTable = (props: PayoutsPoolTableProps) => {
  const { 
    className,
    csvRef,
  } = props;
  const intl = useIntl();
  const { miner, coin } = useMinerAccount();
  const { explorerLink } = useExplorer({ coin });

  const { offset, nextPage } = usePagination({
    limit: LIMIT,
  });

  const {
    data = {},
    isLoading,
    isFetching,
    isSuccess,
  } = useGetTransactionsInfiniteSharedQuery({ 
    miner,
    coin,
    limit: LIMIT,
    sortBy: `${ORDER.DESC}${SORT_BY.CREATED_AT}`,
    offset,
  }, { skip: !(miner && coin) })
  const transactions = _get(data, 'items', []).filter((transaction: any) => !REJECTED_STATUSES.includes(transaction.status));
  const total = _get(data, 'total', 0);

  const handleLoadMore = (inView: boolean) => { 
    // increase pages number
    if (inView && !isLoading) {
      nextPage(total);
    }
  };

  return (
    <>
      {/* Inject CSV download in the parent component */}
      {csvRef?.current ? (
        createPortal((
          <DownloadCsv
            prepareData={() => {
              const csvData = [
                [
                  intl.formatMessage({ id: 'payouts.time' }),
                  intl.formatMessage({ id: 'payouts.txid' }),
                  intl.formatMessage({ id: 'payouts.total' }),
                  intl.formatMessage({ id: 'payouts.status' }),
                ],
              ];
              transactions.forEach((transaction: any) => {
                const { amount, createdAt, txHash, status } = transaction;
                const statusMessage = intl.formatMessage({ id: getStatusMessage(status) })
                const time = formatChartDate({ date: createdAt, locale: intl.locale });
                return csvData.push([time, txHash, amount, statusMessage]);
              });
              return csvData;
            }}
            filename={`${coin} - ${intl.formatMessage({ id: 'payouts.history' })}`}
            title={intl.formatMessage({ id: 'download.csv' })}
          />
        ), csvRef?.current)
      ) : null}

      <Table
        isLoading={isLoading || isFetching}
        LoaderComponent={
          <LoadMore title={intl.formatMessage({ id: 'loader.loading.more' })}/>
        }
        onScrollDown={handleLoadMore}
        stickyHeader={true}
        data={transactions}
        responsiveColspan={{
          md: 3,
        }}
        onRowClick={(row) => {
          row.txHash && openNewTab(explorerLink(row.txHash));
        }}
        columns={[
          {
            Header: intl.formatMessage({ id: 'payouts.time' }),
            accessor: 'createdAt',
            Cell: DateCell,
          }, 
          {
            Header: intl.formatMessage({ id: 'payouts.txid' }),
            accessor: 'txHash',
            disableSortBy: true,
            className: 'hidden md:table-cell',
            Cell: ({ value }: any) => <TxHashCell coin={coin} hash={value} explorer={explorerLink(value)}/>
          },
          {
            Header: intl.formatMessage({ id: 'payouts.total' }),
            accessor: 'amount',
            Cell: ({ value }: any) => <TextCell value={value} subValue={coin}/>
          }, 
          {
            Header: intl.formatMessage({ id: 'payouts.status' }),
            accessor: 'status', 
            Cell: (props: any) => {
              const { value, row, data } = props;
              const hash = _get(data, `${row.index}.txHash`, null);
              return (
                <span className='flex items-center justify-between'>
                  <StatusIcon
                    className='w-[14px] h-[14px]'
                    status={getStatusIconVariant(value)}
                    title={intl.formatMessage({ id: getStatusMessage(value) })}
                    titleClassName='hidden md:inline-block'
                  />
                  <div className="ml-2 w-6 inline-flex items-center">
                    {hash ? (
                      <NewTabLink/>
                    ) : null}
                  </div>
                </span>
              )
            }
          },
        ]}
      />
    </>
  );
}

export default PayoutsPoolTable;
